import {metadaApi} from '@dms/api/base';

import {
  BulkDeleteReceiveNoteRequest,
  BulkDeleteReceiveNoteRequestSchema,
  BulkDeleteReceiveNoteResponse,
  BulkDeleteReceiveNoteResponseSchema,
  GetReceiveNoteFastTrackRequest,
  GetReceiveNoteFastTrackRequestSchema,
  GetReceiveNoteFastTrackResponse,
  GetReceiveNoteFastTrackResponseSchema,
  GetReceiveNoteRequest,
  GetReceiveNoteRequestSchema,
  GetReceiveNoteResponse,
  GetReceiveNoteResponseSchema,
  PatchReceiveNoteBulkEditRequest,
  PatchReceiveNoteBulkEditRequestSchema,
  PatchReceiveNoteBulkEditResponse,
  PatchReceiveNoteBulkEditResponseSchema,
  PatchReceiveNoteCompletedRequest,
  PatchReceiveNoteCompletedRequestSchema,
  PatchReceiveNoteCompletedResponse,
  PatchReceiveNoteCompletedResponseSchema,
  PatchReceiveNoteManualRequest,
  PatchReceiveNoteManualRequestSchema,
  PatchReceiveNoteManualResponse,
  PatchReceiveNoteManualResponseSchema,
  PatchReceiveNoteRequest,
  PatchReceiveNoteRequestSchema,
  PatchReceiveNoteResponse,
  PatchReceiveNoteResponseSchema,
  PatchReceiveNoteStateRequest,
  PatchReceiveNoteStateRequestSchema,
  PatchReceiveNoteStateResponse,
  PatchReceiveNoteStateResponseSchema,
  PostReceiveNoteManualRequest,
  PostReceiveNoteManualRequestSchema,
  PostReceiveNoteManualResponse,
  PostReceiveNoteManualResponseSchema,
} from './types';

export const metadaWarehouseReceiveNoteApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getReceiveNote: build.query<GetReceiveNoteResponse, GetReceiveNoteRequest>({
      query: (queryArg) => ({
        url: `warehouse/v5/receive-note/${queryArg.receiveNoteId}`,
        queryArg,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'receiveNote', id: queryArg.receiveNoteId},
      ],
      extraOptions: {
        requestSchema: GetReceiveNoteRequestSchema,
        responseSchema: GetReceiveNoteResponseSchema,
      },
    }),
    postReceiveNoteManual: build.mutation<
      PostReceiveNoteManualResponse,
      PostReceiveNoteManualRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/manual`,
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      invalidatesTags: ['receiveNote'],
      extraOptions: {
        requestSchema: PostReceiveNoteManualRequestSchema,
        responseSchema: PostReceiveNoteManualResponseSchema,
      },
    }),
    patchReceiveNoteManual: build.mutation<
      PatchReceiveNoteManualResponse,
      PatchReceiveNoteManualRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v2/receive-note/${queryArg.receiveNoteId}/manual`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'receiveNote', id: queryArg.receiveNoteId},
        {type: 'deliveryNote', id: result?.deliveryNote.deliveryNoteId},
      ],
      extraOptions: {
        requestSchema: PatchReceiveNoteManualRequestSchema,
        responseSchema: PatchReceiveNoteManualResponseSchema,
      },
    }),
    patchReceiveNoteCompleted: build.mutation<
      PatchReceiveNoteCompletedResponse,
      PatchReceiveNoteCompletedRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/completed`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'receiveNote', id: queryArg.receiveNoteId},
        'deliveryNote',
      ],
      extraOptions: {
        requestSchema: PatchReceiveNoteCompletedRequestSchema,
        responseSchema: PatchReceiveNoteCompletedResponseSchema,
      },
    }),
    bulkDeleteReceiveNote: build.mutation<
      BulkDeleteReceiveNoteResponse,
      BulkDeleteReceiveNoteRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v1/receive-note/delete',
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      invalidatesTags: ['receiveNote', 'deliveryNote'],
      extraOptions: {
        requestSchema: BulkDeleteReceiveNoteRequestSchema,
        responseSchema: BulkDeleteReceiveNoteResponseSchema,
      },
    }),
    patchReceiveNoteState: build.mutation<
      PatchReceiveNoteStateResponse,
      PatchReceiveNoteStateRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/state`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'receiveNote', id: queryArg.receiveNoteId},
      ],
      extraOptions: {
        requestSchema: PatchReceiveNoteStateRequestSchema,
        responseSchema: PatchReceiveNoteStateResponseSchema,
      },
    }),
    patchReceiveNoteManualState: build.mutation<
      PatchReceiveNoteStateResponse,
      PatchReceiveNoteStateRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/manual/state`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: ['receiveNote', 'receiveNoteFastTrack'],
      extraOptions: {
        requestSchema: PatchReceiveNoteStateRequestSchema,
        responseSchema: PatchReceiveNoteStateResponseSchema,
      },
    }),
    getReceiveNoteFastTrack: build.query<
      GetReceiveNoteFastTrackResponse,
      GetReceiveNoteFastTrackRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/fast-track`,
        queryArg,
      }),
      providesTags: ['receiveNoteFastTrack'],
      extraOptions: {
        requestSchema: GetReceiveNoteFastTrackRequestSchema,
        responseSchema: GetReceiveNoteFastTrackResponseSchema,
      },
    }),
    patchReceiveNoteFastTrack: build.mutation<PatchReceiveNoteResponse, PatchReceiveNoteRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/fast-track`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: ['receiveNoteFastTrack', 'headerDynamicActions'],
      extraOptions: {
        requestSchema: PatchReceiveNoteRequestSchema,
        responseSchema: PatchReceiveNoteResponseSchema,
      },
    }),
    patchReceiveNoteBulkEdit: build.mutation<
      PatchReceiveNoteBulkEditResponse,
      PatchReceiveNoteBulkEditRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v1/receive-note/bulk-edit',
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: ['receiveNote'],
      extraOptions: {
        requestSchema: PatchReceiveNoteBulkEditRequestSchema,
        responseSchema: PatchReceiveNoteBulkEditResponseSchema,
      },
    }),
  }),
});

export const {
  useGetReceiveNoteQuery,
  useLazyGetReceiveNoteQuery,
  usePostReceiveNoteManualMutation,
  usePatchReceiveNoteManualMutation,
  usePatchReceiveNoteCompletedMutation,
  useBulkDeleteReceiveNoteMutation,
  usePatchReceiveNoteStateMutation,
  usePatchReceiveNoteManualStateMutation,
  useGetReceiveNoteFastTrackQuery,
  usePatchReceiveNoteFastTrackMutation,
  usePatchReceiveNoteBulkEditMutation,
} = metadaWarehouseReceiveNoteApi;
